/**
 * 支付宝支付服务
 */
import api from './api';

/**
 * 预览充值信息
 * @param {number} amount - 充值金额（元）
 * @returns {Promise} 充值预览信息
 */
export const previewRecharge = async (amount) => {
  try {
    const response = await api.post('/api/v1/alipay/recharge/preview', {
      amount: parseFloat(amount)
    });
    return response.data;
  } catch (error) {
    console.error('预览充值失败:', error);
    throw error;
  }
};

/**
 * 创建支付订单
 * @param {number} amount - 充值金额（元）
 * @param {string} paymentType - 支付类型：'page'(电脑网站支付) 或 'wap'(手机网站支付)
 * @returns {Promise} 订单信息，包含支付URL
 */
export const createOrder = async (amount, paymentType = 'page') => {
  try {
    const response = await api.post('/api/v1/alipay/order/create', {
      amount: parseFloat(amount),
      payment_type: paymentType
    });
    return response.data;
  } catch (error) {
    console.error('创建订单失败:', error);
    throw error;
  }
};

/**
 * 查询订单状态
 * @param {string} outTradeNo - 商户订单号
 * @returns {Promise} 订单状态信息
 */
export const getOrderStatus = async (outTradeNo) => {
  try {
    const response = await api.get(`/api/v1/alipay/order/status/${outTradeNo}`);
    return response.data;
  } catch (error) {
    console.error('查询订单状态失败:', error);
    throw error;
  }
};

/**
 * 获取用户的充值订单列表
 * @param {number} limit - 限制返回数量
 * @param {number} offset - 偏移量
 * @returns {Promise} 订单列表
 */
export const getUserOrders = async (limit = 10, offset = 0) => {
  try {
    const response = await api.get('/api/v1/alipay/orders', {
      params: { limit, offset }
    });
    return response.data;
  } catch (error) {
    console.error('获取订单列表失败:', error);
    throw error;
  }
};

/**
 * 打开支付宝支付页面
 * @param {string} paymentUrl - 支付URL
 */
export const openPayment = (paymentUrl) => {
  // 在新窗口打开支付页面
  window.open(paymentUrl, '_blank');
};

/**
 * 轮询订单状态
 * @param {string} outTradeNo - 商户订单号
 * @param {function} onSuccess - 支付成功回调
 * @param {function} onFailed - 支付失败回调
 * @param {number} maxAttempts - 最大尝试次数
 * @param {number} interval - 轮询间隔（毫秒）
 */
export const pollOrderStatus = (
  outTradeNo,
  onSuccess,
  onFailed,
  maxAttempts = 60,
  interval = 3000
) => {
  let attempts = 0;
  
  const poll = async () => {
    attempts++;
    
    try {
      const orderStatus = await getOrderStatus(outTradeNo);
      
      if (orderStatus.status === 'success') {
        // 支付成功
        if (onSuccess) {
          onSuccess(orderStatus);
        }
        return;
      } else if (orderStatus.status === 'failed' || orderStatus.status === 'cancelled') {
        // 支付失败或取消
        if (onFailed) {
          onFailed(orderStatus);
        }
        return;
      } else if (attempts >= maxAttempts) {
        // 超过最大尝试次数
        if (onFailed) {
          onFailed({ ...orderStatus, error: '查询超时' });
        }
        return;
      } else {
        // 继续轮询
        setTimeout(poll, interval);
      }
    } catch (error) {
      console.error('轮询订单状态失败:', error);
      if (attempts >= maxAttempts) {
        if (onFailed) {
          onFailed({ error: '查询失败' });
        }
      } else {
        // 继续轮询
        setTimeout(poll, interval);
      }
    }
  };
  
  // 开始轮询
  poll();
};

/**
 * 支付宝充值流程
 * @param {number} amount - 充值金额
 * @param {function} onSuccess - 成功回调
 * @param {function} onError - 错误回调
 * @param {string} paymentType - 支付类型
 */
export const rechargeWithAlipay = async (
  amount,
  onSuccess,
  onError,
  paymentType = 'page'
) => {
  try {
    // 1. 创建订单
    const order = await createOrder(amount, paymentType);
    
    // 2. 打开支付页面
    openPayment(order.payment_url);
    
    // 3. 轮询订单状态
    pollOrderStatus(
      order.out_trade_no,
      (orderStatus) => {
        if (onSuccess) {
          onSuccess(orderStatus);
        }
      },
      (error) => {
        if (onError) {
          onError(error);
        }
      }
    );
    
    return order;
  } catch (error) {
    console.error('支付宝充值失败:', error);
    if (onError) {
      onError(error);
    }
    throw error;
  }
};

export default {
  previewRecharge,
  createOrder,
  getOrderStatus,
  getUserOrders,
  openPayment,
  pollOrderStatus,
  rechargeWithAlipay
};

